import { useState, useEffect, useMemo } from 'react';
import { Modal, Select, Button, Spin } from 'antd';
import { getAllUsersAndAccounts } from '../../services/users';
import type { Account, User } from 'src/types/user';
import { transferProjectToAccount } from 'src/services/projects';
import { toast } from 'sonner';

const { Option } = Select;

interface Props {
  open: boolean;
  onClose: () => void;
  parentProjectId: string;
  onDeleteParentProjectLocally: (parentProjectId: string) => void;
}

export default function TransferToAccountModalLocally({ open, onClose, parentProjectId, onDeleteParentProjectLocally }: Props) {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>();
  const [selectedUser, setSelectedUser] = useState<string>();
  const [transfering, setTransfering] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setLoading(true);
    getAllUsersAndAccounts(true)
      .then((data) => {
        setAccounts(data.accounts);
        setUsers(data.users);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open]);

  const filteredUsers = useMemo(() => {
    if (!selectedAccount) {
      return users;
    }
    return users.filter((u) => u.accountsAccess.some((a) => a.accountId === selectedAccount));
  }, [users, selectedAccount]);

  const filteredAccounts = useMemo(() => {
    if (!selectedUser) {
      return accounts;
    }
    const user = users.find((u) => u.email === selectedUser);
    if (!user) {
      return [];
    }
    const allowed = new Set(user.accountsAccess.map((a) => a.accountId));
    return accounts.filter((a) => allowed.has(a.accountId));
  }, [accounts, users, selectedUser]);

  useEffect(() => {
    if (!selectedUser || !selectedAccount) {
      return;
    }
    const stillValid = filteredAccounts.some((a) => a.accountId === selectedAccount);
    if (!stillValid) {
      setSelectedAccount(undefined);
    }
  }, [selectedUser, filteredAccounts, selectedAccount]);

  useEffect(() => {
    if (!selectedUser || !selectedAccount) {
      return;
    }
    const stillValid = filteredUsers.some((u) => u.email === selectedUser);
    if (!stillValid) {
      setSelectedUser(undefined);
    }
  }, [selectedAccount, filteredUsers, selectedUser]);

  const handleClose = () => {
    setSelectedAccount(undefined);
    setSelectedUser(undefined);
    onClose();
  }

  const handleTransfer = async () => {
    if (!selectedAccount || !selectedUser) {
      return;
    }
    setTransfering(true);
    try {
      await transferProjectToAccount(parentProjectId, selectedAccount, selectedUser);
      toast.success('Project transferred successfully');
      onDeleteParentProjectLocally(parentProjectId);
      handleClose();
    } catch {
      toast.error('Failed to transfer project');
    } finally {
      setTransfering(false);
    }
  };

  return (
    <Modal title='Transfer Project' open={open} onCancel={handleClose} footer={null}>
      {loading ? (
        <Spin tip='Loading data...' />
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <Select
              showSearch
              allowClear
              placeholder='Select Account'
              style={{ width: '100%' }}
              value={selectedAccount}
              onChange={setSelectedAccount}
              optionFilterProp='children'
            >
              {filteredAccounts.map((a) => (
                <Option key={a.accountId} value={a.accountId}>
                  {a.accountName} ({a.accountType})
                </Option>
              ))}
            </Select>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Select
              showSearch
              allowClear
              placeholder='Select User'
              style={{ width: '100%' }}
              value={selectedUser}
              onChange={setSelectedUser}
              optionFilterProp='children'
            >
              {filteredUsers.map((u) => (
                <Option key={u.email} value={u.email}>
                  {u.firstName} {u.lastName} ({u.email}) [{u.role}]
                </Option>
              ))}
            </Select>
          </div>
          <Button
            type='primary'
            block
            disabled={!selectedAccount || !selectedUser}
            loading={transfering}
            onClick={handleTransfer}
          >
            Transfer
          </Button>
        </>
      )}
    </Modal>
  );
}
